import * as React from "react";
import { Infinity as InfinityIcon, Minus, Plus } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * A compact numeric stepper — the "flywheel" alternative to typing into a box.
 * −/+ buttons with press-and-hold to repeat (accelerating), the value still
 * directly editable, and an optional ∞ toggle that maps to {@code -1} (unlimited).
 * Values stay in [min, max].
 */
export function Stepper({
  value,
  onChange,
  min = 0,
  max = 9999,
  step = 1,
  allowUnlimited = false,
  ariaLabel,
}: {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  allowUnlimited?: boolean;
  ariaLabel?: string;
}) {
  const unlimited = allowUnlimited && value < 0;
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const clamp = (n: number) => Math.max(min, Math.min(max, n));

  const bump = (dir: 1 | -1) => onChange(clamp((value < 0 ? 0 : value) + dir * step));

  // Press-and-hold to repeat, accelerating from 300ms → 60ms. onClick fires the
  // first step; the hold drives the rest off a local mirror (onChange takes a value).
  const startHold = (dir: 1 | -1) => {
    if (unlimited) return;
    let current = value < 0 ? 0 : value;
    let delay = 300;
    const run = () => {
      current = clamp(current + dir * step);
      onChange(current);
      delay = Math.max(60, delay - 40);
      timer.current = setTimeout(run, delay);
    };
    timer.current = setTimeout(run, delay);
  };
  const stopHold = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  React.useEffect(() => stopHold, []);

  return (
    <div className="inline-flex items-center gap-1">
      <div
        className={cn(
          "inline-flex items-center rounded-full border border-border bg-background p-0.5 shadow-sm transition",
          unlimited && "opacity-50",
        )}
      >
        <StepBtn
          label="Decrease"
          disabled={unlimited || value <= min}
          onClick={() => bump(-1)}
          onHoldStart={() => startHold(-1)}
          onHoldEnd={stopHold}
        >
          <Minus className="h-3.5 w-3.5" />
        </StepBtn>

        <input
          aria-label={ariaLabel}
          value={unlimited ? "∞" : value}
          readOnly={unlimited}
          inputMode="numeric"
          onChange={(e) => {
            const n = parseInt(e.target.value.replace(/[^\d]/g, ""), 10);
            onChange(Number.isNaN(n) ? min : clamp(n));
          }}
          className="w-12 border-0 bg-transparent text-center font-display text-sm font-semibold tabular-nums focus:outline-none"
        />

        <StepBtn
          label="Increase"
          disabled={unlimited || value >= max}
          onClick={() => bump(1)}
          onHoldStart={() => startHold(1)}
          onHoldEnd={stopHold}
        >
          <Plus className="h-3.5 w-3.5" />
        </StepBtn>
      </div>

      {allowUnlimited && (
        <button
          type="button"
          aria-pressed={unlimited}
          title="Unlimited"
          onClick={() => onChange(unlimited ? Math.max(min, 1) : -1)}
          className={cn(
            "grid h-8 w-8 place-items-center rounded-full border transition",
            unlimited
              ? "border-teal-400 bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-300"
              : "border-border bg-background text-muted-foreground hover:bg-muted",
          )}
        >
          <InfinityIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function StepBtn({
  label,
  disabled,
  onClick,
  onHoldStart,
  onHoldEnd,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  onHoldStart: () => void;
  onHoldEnd: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      onPointerDown={onHoldStart}
      onPointerUp={onHoldEnd}
      onPointerLeave={onHoldEnd}
      className="grid h-7 w-7 place-items-center rounded-full text-foreground transition hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}
